import { useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import {
  ArrowRight,
  Check,
  ChevronRight,
  CircleHelp,
  Mail,
  MessageCircle,
  Users,
  Workflow,
} from "lucide-react"

type StepTarget =
  | "/w/$workspace/settings/general"
  | "/w/$workspace/settings/channels"
  | "/w/$workspace/settings/team"
  | "/w/$workspace/automations"

const TEMPLATE_PATH = [
  { icon: MessageCircle, text: "Someone comments GUIDE" },
  { icon: Mail, text: "Send the studio guide" },
  { icon: Users, text: "Hand over to your team" },
]
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { Heading } from "@/components/workspace/primitives"
import { automationsQuery, channelsQuery, teamQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"
import "@/styles/onboarding-polish.css"

export const Route = createFileRoute("/w/$workspace/getting-started")({
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(teamQuery(params.workspace)),
      context.queryClient.ensureQueryData(channelsQuery(params.workspace)),
      context.queryClient.ensureQueryData(automationsQuery(params.workspace)),
    ]),
  component: GettingStarted,
})

/** Completed setup steps come from workspace data; an inbox tour is offered separately. */
function GettingStarted() {
  const { workspace } = useWorkspaceContext()
  const team = useSuspenseQuery(teamQuery(workspace.slug)).data
  const { connections } = useSuspenseQuery(channelsQuery(workspace.slug)).data
  const { automations } = useSuspenseQuery(automationsQuery(workspace.slug)).data
  const params = { workspace: workspace.slug }
  const teammates = team.members.length - 1
  const pending = team.invitations.length
  const connectedChannels = connections.filter(
    (connection) => connection.status === "connected"
  ).length
  // A paused automation still has a published version and has met this setup milestone.
  const publishedAutomations = automations.filter(
    (automation) => automation.liveVersion !== null
  ).length
  const checklist: Array<{ title: string; detail: string; done: boolean; to: StepTarget }> = [
    {
      title: "Create your workspace",
      detail: `${workspace.name} · ${workspace.timezone}`,
      done: true,
      to: "/w/$workspace/settings/general",
    },
    {
      title: "Connect your channels",
      detail: connectedChannels
        ? `${connectedChannels} channel${connectedChannels === 1 ? " is" : "s are"} connected and ready.`
        : connections.length
          ? "Review channel access to start receiving conversations."
          : "Bring Instagram or Messenger into one inbox.",
      done: connectedChannels > 0,
      to: "/w/$workspace/settings/channels",
    },
    {
      title: "Invite your team",
      detail:
        teammates > 0
          ? `${teammates} teammate${teammates === 1 ? " has" : "s have"} joined your workspace.${pending ? ` ${pending} invitation${pending === 1 ? "" : "s"} pending.` : ""}`
          : pending
            ? `${pending} invitation${pending === 1 ? "" : "s"} waiting to be accepted.`
            : "Add the people who reply, publish and manage.",
      done: teammates > 0,
      to: "/w/$workspace/settings/team",
    },
    {
      title: "Publish your first automation",
      detail: publishedAutomations
        ? `${publishedAutomations} automation${publishedAutomations === 1 ? " has" : "s have"} a published version.`
        : "Start with a tested comment-to-DM template.",
      done: publishedAutomations > 0,
      to: "/w/$workspace/automations",
    },
  ]
  const complete = checklist.filter((step) => step.done).length
  const nextStep = checklist.findIndex((step) => !step.done)
  return (
    <div className="page-wrap ws-workspace onboarding-polish">
      <Heading
        title="A little setup. A better workday."
        description={`Welcome to ${workspace.name}. Let’s get everything ready for your team.`}
        action={
          <Button variant="outline" asChild>
            <Link to="/w/$workspace/overview" params={params}>
              Go to overview
              <ArrowRight />
            </Link>
          </Button>
        }
      />
      <div className="ws-onboarding-grid">
        <Card className="ws-checklist">
          <CardHeader className="onboarding-checklist-header">
            <span className="onboarding-eyebrow">Make yourself at home</span>
            <div className="onboarding-checklist-heading">
              <CardTitle>Your launch checklist</CardTitle>
              <Badge className="onboarding-progress-badge">
                {complete}/{checklist.length} complete
              </Badge>
            </div>
            <CardDescription>Four setup steps, with progress from your workspace.</CardDescription>
            <Progress
              value={(complete / checklist.length) * 100}
              className="onboarding-progress"
              aria-label="Launch checklist completion"
            />
          </CardHeader>
          <CardContent className="onboarding-checklist-content">
            {checklist.map((step, index) => (
              <div
                className={`ws-checklist-row ${index === nextStep ? "is-next" : ""}`}
                key={step.title}
              >
                <span
                  className={`ws-step-marker ${step.done ? "done" : ""}`}
                  aria-label={step.done ? "Complete" : `Step ${index + 1}`}
                >
                  {step.done ? <Check size={15} aria-hidden="true" /> : index + 1}
                </span>
                <div>
                  <div className="onboarding-step-title">
                    <strong>{step.title}</strong>
                    {index === nextStep && <span className="onboarding-next-label">Up next</span>}
                  </div>
                  <p>{step.detail}</p>
                </div>
                <Button
                  variant={step.done ? "ghost" : index === nextStep ? "default" : "outline"}
                  asChild
                >
                  <Link to={step.to} params={params}>
                    {step.done ? "View" : "Continue"}
                    <ChevronRight />
                  </Link>
                </Button>
              </div>
            ))}
            <div className="onboarding-inbox-tour">
              <span className="onboarding-tour-icon">
                <MessageCircle size={17} aria-hidden="true" />
              </span>
              <div>
                <strong>Meet your shared inbox</strong>
                <p>Explore replies, assignments and internal notes at your own pace.</p>
              </div>
              <Button variant="ghost" asChild>
                <Link to="/w/$workspace/inbox" params={params}>
                  Explore <ArrowRight />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
        <div className="ws-onboarding-aside">
          <Card className="onboarding-feature">
            <div className="onboarding-feature-art">
              <div className="setup-flow-art" aria-hidden="true">
                <span>
                  <MessageCircle size={20} />
                </span>
                <ArrowRight size={16} />
                <span>
                  <Mail size={20} />
                </span>
                <ArrowRight size={16} />
                <span>
                  <Users size={20} />
                </span>
              </div>
              <span className="onboarding-template-label">
                <Workflow size={13} aria-hidden="true" /> Start with a template
              </span>
            </div>
            <CardHeader>
              <CardTitle>A first flow worth trying</CardTitle>
              <CardDescription>
                Turn a “GUIDE” comment into a helpful private reply. Bring in your team when a human
                touch is needed.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="ws-template-path">
                {TEMPLATE_PATH.map(({ icon: Icon, text }) => (
                  <div key={text}>
                    <span className="onboarding-path-icon">
                      <Icon size={15} aria-hidden="true" />
                    </span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>
              <Button className="w-full" asChild>
                <Link to="/w/$workspace/automations/templates" params={params}>
                  Explore the template
                  <ArrowRight />
                </Link>
              </Button>
            </CardContent>
          </Card>
          <div className="ws-quiet-help">
            <CircleHelp size={17} aria-hidden="true" />
            <div>
              <strong>Before you go live</strong>
              <p>Check channel access, test your flow, and review the human handover.</p>
              <Link to="/w/$workspace/automations" params={params}>
                Review your automations
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
